/** @type {Record<string, string>} */
export const TICKET_STATUS_CLASS = {
  "Pending Review":    "bg-[var(--color-primary)]/12 text-[var(--color-primary)]",
  "Under Review":      "bg-[var(--color-primary)]/12 text-[var(--color-primary)]",
  "On Hold":           "bg-[var(--color-warning)]/12 text-[var(--color-warning)]",
  Accepted:            "bg-[var(--color-success)]/12 text-[var(--color-success)]",
  Scheduled:           "bg-[var(--color-accent)]/12 text-[var(--color-accent)]",
  "In Progress":       "bg-[var(--color-accent)]/12 text-[var(--color-accent)]",
  "Quotation Submitted": "bg-[var(--color-accent)]/12 text-[var(--color-accent)]",
  "Quotation Accepted":  "bg-[var(--color-success)]/12 text-[var(--color-success)]",
  "Closure Requested": "bg-[var(--color-warning)]/12 text-[var(--color-warning)]",
  "Closure Verified":  "bg-[var(--color-accent)]/12 text-[var(--color-accent)]",
  Closed:              "bg-[var(--color-success)]/12 text-[var(--color-success)]",
  Rejected:            "bg-[var(--color-error)]/12 text-[var(--color-error)]",
  Assigned:            "bg-[var(--color-primary)]/12 text-[var(--color-primary)]",
};

/** i18n keys under `fsm` for ticket status display labels. */
const TICKET_STATUS_I18N_KEYS = {
  pending_review: "fsm.statusPendingReview",
  under_review: "fsm.statusUnderReview",
  on_hold: "fsm.statusOnHold",
  hold: "fsm.statusOnHold",
  assigned: "fsm.statusAssigned",
  accepted: "fsm.statusAccepted",
  scheduled: "fsm.statusScheduled",
  in_progress: "fsm.statusInProgress",
  quotation_submitted: "fsm.statusQuotationSubmitted",
  quotation_accepted: "fsm.statusQuotationAccepted",
  closure_requested: "fsm.statusClosureRequested",
  closure_request: "fsm.statusClosureRequested",
  closure_verified: "fsm.statusClosureVerified",
  closed: "fsm.statusClosed",
  rejected: "status.rejected",
  reject: "status.rejected",
  proceed: "fsm.statusAssigned",
};

/**
 * Resolve fsm.* i18n key for a ticket status (API code or English label).
 * @param {string} status
 * @returns {string|null}
 */
export function getTicketStatusI18nKey(status) {
  if (status == null || status === "") return TICKET_STATUS_I18N_KEYS.under_review;
  const key = String(status)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return TICKET_STATUS_I18N_KEYS[key] || null;
}

/**
 * Translate a ticket status for display. Falls back to the raw label.
 * @param {string} status
 * @param {(key: string) => string} t
 */
export function translateTicketStatus(status, t) {
  const i18nKey = getTicketStatusI18nKey(status);
  if (i18nKey && typeof t === "function") {
    const translated = t(i18nKey);
    if (translated && translated !== i18nKey) return translated;
  }
  return status || "";
}

/** Statuses treated as "closed" for the default open-tickets list. */
export const CLOSED_STATUSES = new Set(["Closed"]);

export function isClosedTicketStatus(status) {
  const key = String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return key === "closed";
}

/** @type {Record<string, string>} */
export const PRIORITY_CLASS = {
  Critical: "text-[var(--color-error)]",
  High:     "text-[var(--color-warning)]",
  Medium:   "text-[var(--color-primary)]",
  Low:      "text-[var(--color-success)]",
};

/** i18n keys under `fsm` for priority display labels. */
const TICKET_PRIORITY_I18N_KEYS = {
  critical: "fsm.critical",
  high: "fsm.high",
  medium: "fsm.medium",
  low: "fsm.low",
};

/**
 * Resolve fsm.* i18n key for a ticket priority (API code or English label).
 * @param {string|number} priority
 * @returns {string|null}
 */
export function getTicketPriorityI18nKey(priority) {
  if (priority == null || priority === "" || priority === "—") return null;
  const raw = String(priority).trim().toLowerCase();
  const fromCode = {
    "1": "critical",
    "2": "high",
    "3": "medium",
    "4": "low",
  };
  const key = fromCode[raw] || raw;
  return TICKET_PRIORITY_I18N_KEYS[key] || null;
}

/**
 * Translate a ticket priority for display. Falls back to the raw label.
 * @param {string|number} priority
 * @param {(key: string) => string} t
 */
export function translateTicketPriority(priority, t) {
  const i18nKey = getTicketPriorityI18nKey(priority);
  if (i18nKey && typeof t === "function") {
    const translated = t(i18nKey);
    if (translated && translated !== i18nKey) return translated;
  }
  if (priority == null || priority === "") return "";
  return String(priority);
}

/** CSS class for a priority label (case-insensitive; supports numeric codes). */
export function getPriorityClass(priority) {
  const raw = String(priority ?? "").trim();
  const fromCode = {
    "1": "Critical",
    "2": "High",
    "3": "Medium",
    "4": "Low",
  };
  const key = fromCode[raw] || raw;
  if (PRIORITY_CLASS[key]) return PRIORITY_CLASS[key];
  const match = Object.keys(PRIORITY_CLASS).find(
    (k) => k.toLowerCase() === key.toLowerCase()
  );
  return match ? PRIORITY_CLASS[match] : "text-[var(--color-text-secondary)]";
}
